import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { creditInsuranceCoverageOptions, loanProtectionCoverageOptions } from '@ph-static-data/coverage-options';
import { Subscription } from 'rxjs';

import { ContractSaveFacade } from '@ph-core/services/contract-save.facade';
import { PhDialog } from '@ph-core/services/dialog';
import { FormService } from '@ph-core/services/form.service';
import { NotificationService } from '@ph-core/services/notification';
import { QuoteContainerService } from '@ph-core/services/quote-container.service';
import { LazyTemplateService } from '@ph-core/services/template/lazy-template.service';
import { PostalResponse } from '@ph-model/api/response/postal.response.model';
import { CheckoutFormValues } from '@ph-model/checkout/checkout-form-values.model';
import { Lienholder } from '@ph-model/checkout/submit_cart/response/lienholder';
import { FinanceForm } from '@ph-model/form/finance-form';
import { Markup } from '@ph-model/login';
import { ProductItem } from '@ph-model/response';
import { RemitStore, RemitStoreType } from '@ph-remit/store/remit/remit.store';
import { PhNotificationType } from '@ph-shared/components/notification/notification/notification.interface';
import { IComplexDate } from '@ph-shared/utils/date-control-formater';
import { productIdGenerator } from '@ph-shared/utils/product-id-generator';
import {
  CartItem,
  CartTaxFormValues
} from '@ph-shopping/model/cart';
import {
  ShoppingStepperStore,
  ShoppingStepperStoreType,
} from '@ph-shopping/modules/shopping-stepper/store/shopping-stepper.store';
import { CartApiClient } from '@ph-shopping/store/cart/cart-api.client';
import {
  calculateCartTotal,
  recalculateTaxesAndTotal,
  updateCheckoutForm as patchCheckoutForm,
} from '@ph-shopping/store/cart/cart.helpers';
import { overrideManualCustomerCost, recalculateCustomerCost } from '@ph-shopping/store/product/product.helpers';
import { VehicleStore, VehicleStoreType } from '@ph-shopping/store/vehicle/vehicle.store';
import { PrismicStore, PrismicStoreType } from '@ph-store/prismic/prismic.store';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';

import { withConditionalStorageSync } from '@ph-core/utils/with-conditional-storage-sync';
import { CartStoreRequestsDispatcher } from './cart-store-requests.dispatcher';
import { CartState } from './cart-store.types';
import { withCartMethods } from './methods/cart-store-cart.methods';
import { withCheckoutMethods } from './methods/cart-store-checkout.methods';
import { withCartStoreQuoteMethods } from './methods/cart-store-quote.methods';
import { withCartStoreSubmitMethods } from './methods/cart-store-submit.methods';
import { withCartUMUMethods } from './methods/cart-store-umu-quote.methods';

const storeKey = 'CartStore';

const initialCheckoutForms = {
  addlienholderForm: [],
  cosignerForm: [],
  coverageInfoForm: [],
  creditInsuranceForm: [],
  customerForm: [],
  financeForm: [],
  lienholder: {} as Lienholder,
  lienholderForm: [],
  vehicleForm: [],
};

export const InitialState: CartState = {
  cart: [],
  cartTaxFormValues: [],
  cartTotal: 0,
  checkoutCartContainsError: false,
  checkoutForms: initialCheckoutForms,
  checkoutFormValues: new CheckoutFormValues(),
  cosignerFormEnable: false,
  cosignerPostal: new PostalResponse(),
  creditFormEnable: false,
  creditInsurancePackageCoverageOptions: creditInsuranceCoverageOptions,
  customerPostal: new PostalResponse(),
  error: '',
  exculdingCreditFormValid: false,
  isInValidRequestQuote: true,
  isValidSaveCredit: false,
  loanProtectionCoverageOptions: loanProtectionCoverageOptions,
  requestedQuote: null,
  savedQuote: false,
  submittedCart: [],
  submittedCartSuccess: false,
  success: false,
  total: 0.0,
  validatedCart: [],
};

export const CartStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withConditionalStorageSync({ key: storeKey }),
  withState<CartState>(InitialState),
  withMethods(
    (
      store,
      cartApiClient: CartApiClient = inject(CartApiClient),
      contractSaveFacade: ContractSaveFacade = inject(ContractSaveFacade),
      dialog: PhDialog = inject(PhDialog),
      formService: FormService = inject(FormService),
      httpClient: HttpClient = inject(HttpClient),
      notificationService: NotificationService = inject(NotificationService),
      prismicStore: PrismicStoreType = inject(PrismicStore),
      router: Router = inject(Router),
      shoppingStepperStore: ShoppingStepperStoreType = inject(ShoppingStepperStore),
      templateService: LazyTemplateService = inject(LazyTemplateService),
      translate: TranslateService = inject(TranslateService),
      vehicleStore: VehicleStoreType = inject(VehicleStore),
      userStore: UserStoreType = inject(UserStore),
      remitStore: RemitStoreType = inject(RemitStore),
      quoteContainer: QuoteContainerService = inject(QuoteContainerService)
    ) => {

      const { addToCart, deleteFromCart, reconcileCartWithLoadedProducts } = withCartMethods(store, { userStore, cartApiClient });
      const { getCheckoutForm, addTaxes } = withCheckoutMethods(store, { userStore, cartApiClient });
      const { saveQuote, loadQuote, loadQuoteSuccess, callLoadQuote } = withCartStoreQuoteMethods(store, {
        userStore,
        vehicleStore,
        httpClient,
        quoteContainer,
        shoppingStepperStore,
        router,
        formService,
        cartApiClient,
        dialog,
        translate,
        prismicStore,
        templateService,
      });
      const { saveUMUQuote, saveAsUMUQuoteSuccess, saveUMUQuoteFailure, saveUMUCredit } = withCartUMUMethods(store, { cartApiClient, vehicleStore, dialog, notificationService });
      
      const { submitCart } = withCartStoreSubmitMethods(store, { userStore, vehicleStore, dialog, translate, router, remitStore, httpClient, contractSaveFacade });

      function submitCartFailure(error: string): void {
        notificationService.push({
          type: PhNotificationType.cancel,
          value: { title: 'Warning!', message: error },
        });

        patchState(store, { success: false, error, savedQuote: false });
      }

      // updateCartItemTaxAmounts, addToCart, deleteFromCart
      function onCartChange(): void {
        const cartTotal = store.cartTotal();
        const checkoutValues = store.checkoutFormValues();

        updateCheckoutForm({
          financeForm: {
            ...checkoutValues.financeForm,
            financeAmount: +(vehicleStore.financeForm()?.financeAmount ?? 0) + cartTotal,
          },
        });
      }

      // update finance + products total on financeAmount changes
      function updateFinanceFormValues(values: Partial<FinanceForm>): void {
        const checkoutValues = store.checkoutFormValues();
        const taxValues = store.cartTaxFormValues();

        const cartTotal = taxValues.reduce((total, item) => {
          const taxTotal = item.tax['total'] || item.tax['cost'];

          return total + taxTotal;
        }, 0);

        const grandTotal = +(values.financeAmount ?? 0) + cartTotal;

        updateCheckoutForm({
          financeForm: { ...checkoutValues.financeForm, ...values, financeAmount: grandTotal },
        });
      }

      function updateCheckoutForm(updatedFields: Partial<CheckoutFormValues>): void {
        patchCheckoutForm(
          {
            getCheckoutFormValues: () => store.checkoutFormValues(),
            patchCheckoutFormValues: (values) => patchState(store, { checkoutFormValues: values }),
            vehicleStore,
          },
          updatedFields
        );
      }

      function resetCart(): void {
        patchState(store, { ...InitialState });
        updateCheckoutForm({ financeForm: { ...vehicleStore.financeForm() } as FinanceForm });
      }

      function updateAddCreditInsurance(addInsurance: string): void {
        patchState(store, {
          checkoutFormValues: {
            ...store.checkoutFormValues(),
            creditInsuranceForm: {
              ...store.checkoutFormValues().creditInsuranceForm,
              addInsurance,
            },
          },
        });
      }

      function updateFirstPaymentDate(firstPaymentDate: IComplexDate | null): void {
        patchState(store, {
          checkoutFormValues: {
            ...store.checkoutFormValues(),
            creditInsuranceForm: {
              ...store.checkoutFormValues().creditInsuranceForm,
              firstPaymentDate,
            },
          },
        });
      }

      function updateTaxFormValues(cartTaxFormValues: CartTaxFormValues[]): void {
        patchState(store, { cartTaxFormValues: [...cartTaxFormValues] });
      }

      function toggleCosignerForm(cosignerFormEnable: boolean): void {
        patchState(store, { cosignerFormEnable });
      }

      function clearCustomerPostal(): void {
        patchState(store, { customerPostal: {} });
      }

      function getCustomerPostalSuccess(customerPostal: PostalResponse): void {
        patchState(store, { customerPostal });
      }

      function clearCosignerPostal(): void {
        patchState(store, { cosignerPostal: {} });
      }

      function getCosignerPostalSuccess(cosignerPostal: PostalResponse): void {
        patchState(store, { cosignerPostal });
      }

      function setCreditFormEnable(creditFormEnable: boolean): void {
        patchState(store, { creditFormEnable });
      }

      function updateCartTotal(cartTotal: number): void {
        patchState(store, { cartTotal });
      }

      function updateCartItemError(cartItem: CartItem, error: string | undefined): void {
        patchState(store, {
          validatedCart: store.validatedCart().map((item) => (item.key === cartItem.key ? { ...item, error } : item)),
        });
      }

      function updateCartItem(dest: CartItem, src: Partial<CartItem>): void {
        patchState(store, {
          validatedCart: store.validatedCart().map((item) => (item.key === dest.key ? { ...item, ...src } : item)),
          cartTotal: calculateCartTotal(store.cartTaxFormValues()),
        });
      }

      function recalculateCartTotal(): void {
        patchState(store, {
          cartTotal: calculateCartTotal(store.cartTaxFormValues()),
        });
      }

      function manualCustomerCostOverride(
        product: ProductItem,
        customerCost: string,
        markup: Markup,
        surchargesSelection: { name: string; selected: boolean }[]
      ): void {
        const applyOverride = (itemProduct: ProductItem) =>
          overrideManualCustomerCost(itemProduct, +customerCost, markup, surchargesSelection);

        patchState(store, {
          cart: store.cart().map((item) =>
            productIdGenerator(item.product) === productIdGenerator(product)
              ? {
                ...item,
                product: applyOverride(item.product),
              }
              : item
          ),
          validatedCart: store.validatedCart().map((item) =>
            productIdGenerator(item.product) === productIdGenerator(product)
              ? {
                ...item,
                product: applyOverride(item.product),
              }
              : item
          ),
        });

        store
          .validatedCart()
          .filter((item) => productIdGenerator(item.product) === productIdGenerator(product))
          .forEach((cartItem) => {
            updateCartItemTaxAmounts(
              cartItem,
              recalculateTaxesAndTotal(cartItem.product.customerCost ?? 0, cartItem.customerTaxForm ?? [])
            );
          });
      }

      function recalculateCartCustomerCosts(
        markup: Markup,
        surchargesSelection: { name: string; selected: boolean }[]
      ): void {
        const updatedCart = store.cart().map((cartItem) => ({
          ...cartItem,
          product: recalculateCustomerCost(cartItem.product, markup, surchargesSelection),
        }));
        const updatedValidatedCart = store.validatedCart().map((cartItem) => ({
          ...cartItem,
          product: recalculateCustomerCost(cartItem.product, markup, surchargesSelection),
        }));

        patchState(store, {
          cart: updatedCart,
          validatedCart: updatedValidatedCart,
        });

        updatedValidatedCart.forEach((cartItem) => {
          updateCartItemTaxAmounts(
            cartItem,
            recalculateTaxesAndTotal(cartItem.product.customerCost ?? 0, cartItem.customerTaxForm ?? [])
          );
        });
      }

      function resetCreditInsuranceQuote(): void {
        const { financeAmount, monthlyPayment, apr, loanTerm, amortizationPeriod, residualValue } =
          store.checkoutFormValues().financeForm;

        patchState(store, {
          checkoutFormValues: {
            ...store.checkoutFormValues(),
            creditInsuranceForm: {
              ...store.checkoutFormValues().creditInsuranceForm,
              loanAmount: financeAmount,
              interestRate: apr,
              loanTerm,
              amortizationPeriod,
              monthlyPayment,
              residualValue,
            },
          },
          requestedQuote: null,
        });
      }

      function updateItemProduct(key: string, product: Partial<ProductItem>): void {
        const itemIndex = store.validatedCart().findIndex((i: CartItem) => i.key === key);
        const item = store.validatedCart()[itemIndex];

        patchState(store, {
          validatedCart: [
            ...store.validatedCart().slice(0, itemIndex),
            { ...item, product: { ...item.product, ...product } },
            ...store.validatedCart().slice(itemIndex + 1),
          ],
        });
      }

      function updateCartItemTaxAmounts(cartItem: CartItem, taxValues: Record<string, number>): void {
        const itemToUpdate = store
          .cartTaxFormValues()
          .find((currentTaxValues) => cartItem.key === currentTaxValues.key);
        if (!itemToUpdate) {
          const cartTaxFormValues = [...store.cartTaxFormValues(), { key: cartItem.key, tax: taxValues }];
          patchState(store, { cartTaxFormValues, cartTotal: calculateCartTotal(cartTaxFormValues) });
        } else {
          if (JSON.stringify(itemToUpdate.tax) !== JSON.stringify(taxValues)) {
            const cartTaxFormValues = store
              .cartTaxFormValues()
              .map((currentTaxValues) =>
                cartItem.key === currentTaxValues.key
                  ? { ...currentTaxValues, tax: { ...currentTaxValues.tax, ...taxValues } }
                  : currentTaxValues
              );

            patchState(store, { cartTaxFormValues, cartTotal: calculateCartTotal(cartTaxFormValues) });
          }
        }
      }

      return {
        addTaxes,
        addToCart,
        callLoadQuote,
        deleteFromCart,
        getCheckoutForm,
        reconcileCartWithLoadedProducts,
        clearCosignerPostal,
        getCosignerPostalSuccess,
        clearCustomerPostal,
        getCustomerPostalSuccess,
        loadQuote,
        loadQuoteSuccess,
        manualCustomerCostOverride,
        onCartChange,
        recalculateCartCustomerCosts,
        recalculateCartTotal,
        resetCart,
        resetCreditInsuranceQuote,
        saveAsUMUQuoteSuccess,
        saveQuote,
        saveUMUCredit,
        saveUMUQuote,
        saveUMUQuoteFailure,
        setCreditFormEnable,
        submitCart,
        submitCartFailure,
        toggleCosignerForm,
        updateAddCreditInsurance,
        updateCartItem,
        updateCartItemError,
        updateCartItemTaxAmounts,
        updateCartTotal,
        updateCheckoutForm,
        updateFinanceFormValues,
        updateFirstPaymentDate,
        updateItemProduct,
        updateTaxFormValues,
      };
    }
  ),
  withHooks({
    onInit(store): () => void {
      const cartStoreRequestsDispatcher = inject(CartStoreRequestsDispatcher);
      const subscriptions: Subscription[] = [];

      subscriptions.push(
        cartStoreRequestsDispatcher.resetCartDispatched$.subscribe((cartResetRequested) => {
          if (cartResetRequested) {
            store.resetCart();
          }
        })
      );

      subscriptions.push(
        cartStoreRequestsDispatcher.loadQuoteSuccessDispatched$.subscribe((quote) => {
          if (quote) {
            store.loadQuoteSuccess(quote);
          }
        })
      );

      return () => {
        subscriptions.forEach((sub) => sub.unsubscribe());
      };
    },
  })
);

export type CartStoreType = InstanceType<typeof CartStore>;
